import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { Stack, Grid, Box, Divider, Typography } from '@mui/material'
import Select from 'components/generic/form/Select'
import { LOCATION_TYPES } from 'utils/constants'

const AddressDefaultsForm = () => {
  const { control, formState, watch } = useFormContext()
  const { errors } = formState

  const locations = watch('locations')

  const { defaultCorporateLocationOptions, defaultShippingLocationOptions, defaultPayToLocationOptions } =
    useMemo(() => {
      if (!locations)
        return {
          defaultCorporateLocationOptions: [],
          defaultShippingLocationOptions: [],
          defaultPayToLocationOptions: [],
        }
      const corporateLocations: any[] = []
      const shippingLocations: any[] = []
      const payToLocations: any[] = []
      locations.forEach(location => {
        const formattedLocation = {
          ...location,
          name: location?.name || 'Unknown Location Name',
        }
        if (formattedLocation.isCorporate || formattedLocation.locationTypes?.indexOf(LOCATION_TYPES.CORPORATE.id) > -1)
          corporateLocations.push(formattedLocation)
        if (formattedLocation.isShipping || formattedLocation.locationTypes?.indexOf(LOCATION_TYPES.SHIPPING.id) > -1)
          shippingLocations.push(formattedLocation)
        if (
          formattedLocation.isPayToLocation ||
          formattedLocation.locationTypes?.indexOf(LOCATION_TYPES.PAY_TO.id) > -1
        )
          payToLocations.push(formattedLocation)
      })
      return {
        defaultCorporateLocationOptions: corporateLocations,
        defaultShippingLocationOptions: shippingLocations,
        defaultPayToLocationOptions: payToLocations,
      }
    }, [locations])

  const contacts = watch('contacts')

  const contactOptions = useMemo(() => {
    if (!contacts) return []
    return contacts.map(contact => {
      const firstName = contact?.firstName || contact?.firstNameLegal
      const lastName = contact?.lastName || contact?.lastNameLegal
      const combinedName = [firstName, lastName].join(' ').trim()
      return {
        id: contact.id,
        name: combinedName || 'Unknown Contact Name',
      }
    })
  }, [contacts])

  return (
    <Stack flexGrow={1} sx={{ pl: 3, pr: 4 }}>
      <Box>
        <Typography variant="h6" color="textSecondary">
          Set Address Defaults
        </Typography>
        <Box py={1}>
          <Divider />
        </Box>
        <Grid container mt={1} spacing={1}>
          <Grid item md={12} xs={12}>
            <Select
              name="defaultCorporateLocation"
              label="Default Corporate Location"
              defaultValue=""
              control={control}
              items={defaultCorporateLocationOptions}
              error={!!errors.defaultCorporateLocation}
              helperText={errors.defaultCorporateLocation?.message}
              clearable
            />
          </Grid>
        </Grid>
        <Grid container mt={1} spacing={2}>
          <Grid item md={12} xs={12}>
            <Select
              name="defaultShippingLocation"
              label="Default Shipping Location"
              defaultValue=""
              control={control}
              items={defaultShippingLocationOptions}
              error={!!errors.defaultShippingLocation}
              helperText={errors.defaultShippingLocation?.message}
              clearable
            />
          </Grid>
        </Grid>
        <Grid container mt={1} spacing={2}>
          <Grid item md={12} xs={12}>
            <Select
              name="defaultPayToLocation"
              label="Default Pay-To Location"
              defaultValue=""
              control={control}
              items={defaultPayToLocationOptions}
              error={!!errors.defaultPayToLocation}
              helperText={errors.defaultPayToLocation?.message}
              clearable
            />
          </Grid>
        </Grid>
        <Grid container mt={1} spacing={2}>
          <Grid item md={12} xs={12}>
            <Select
              name="defaultPayToContact"
              label="Default Pay-To Contact"
              defaultValue=""
              control={control}
              items={contactOptions}
              error={!!errors.defaultPayToContact}
              helperText="This is the default contact that will show as the payee name on checks"
              clearable
            />
          </Grid>
        </Grid>
      </Box>
    </Stack>
  )
}

export default AddressDefaultsForm
